/* ============================================================
   Cloud Store · PDP reviews custom carousel (V6 v4 — dual mode)
   Modes : editorial (asymmetric, big-number) si ≤ 6 avis,
           columns marquee si ≥ 7 avis.
   Source : Judge.me public widget API.
   ============================================================ */
(function () {
  if (window.__csReviewsCarouselInit) return;
  window.__csReviewsCarouselInit = true;

  const SELECTORS = {
    section: '[data-cs-reviews-section]',
    source: '[data-cs-reviews-source]',
    summary: '[data-cs-reviews-summary]',
    stack: '[data-cs-reviews-stack]',
    stackWrap: '[data-cs-reviews-stack-wrap]',
    dots: '[data-cs-reviews-dots]',
    cta: '[data-cs-reviews-cta]',
    nav: '[data-cs-reviews-nav]',
  };

  const COLORS = ['#6B4FB8', '#5E2F4D', '#5C7A55', '#C99A3D', '#A78BD8', '#7A5DC4', '#A8B89A'];
  const EDITORIAL_THRESHOLD = 6;       // ≤ 6 reviews = editorial mode
  const EDITORIAL_AUTOPLAY_MS = 6000;

  function initialFromName(name) {
    const trimmed = (name || '').trim();
    if (!trimmed) return 'C';
    const first = trimmed.charAt(0).toUpperCase();
    return /[A-Z0-9]/.test(first) ? first : 'C';
  }

  function colorFromName(name) {
    let hash = 0;
    const s = name || 'anon';
    for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
    return COLORS[Math.abs(hash) % COLORS.length];
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function formatDateFR(input) {
    if (!input) return '';
    const d = new Date(input);
    if (isNaN(d.getTime())) return String(input).trim();
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  async function fetchWidgetHTML({ token, shopDomain, productId, perPage = 50 }) {
    const url = `https://judge.me/api/v1/widgets/product_review`
      + `?api_token=${encodeURIComponent(token)}`
      + `&shop_domain=${encodeURIComponent(shopDomain)}`
      + `&external_id=${encodeURIComponent(productId)}`
      + `&per_page=${perPage}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Judge.me API ${res.status}`);
    const json = await res.json();
    return json.widget || '';
  }

  function parseReviewsFromHTML(htmlString) {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const reviews = [];
    doc.querySelectorAll('.jdgm-rev').forEach((el) => {
      const author = (el.querySelector('.jdgm-rev__author') || {}).textContent || 'Anonyme';
      const dateRaw = el.getAttribute('data-published-at')
        || el.getAttribute('data-created-at')
        || (el.querySelector('.jdgm-rev__timestamp') || {}).textContent || '';
      const title = (el.querySelector('.jdgm-rev__title') || {}).textContent || '';
      const body = (el.querySelector('.jdgm-rev__body') || {}).textContent || '';
      const ratingAttr = el.getAttribute('data-score') || el.getAttribute('data-rating');
      let rating = parseInt(ratingAttr, 10);
      if (isNaN(rating)) {
        rating = el.querySelectorAll('.jdgm-star.jdgm--on').length;
        if (!rating) rating = el.querySelectorAll('.jdgm-star:not(.jdgm--off)').length;
      }
      reviews.push({
        author: author.trim(),
        date: formatDateFR(dateRaw),
        title: title.trim(),
        body: body.trim(),
        rating: rating || 5,
      });
    });

    const widgetEl = doc.querySelector('.jdgm-rev-widg');
    let avg = 0, count = 0;
    if (widgetEl) {
      avg = parseFloat(widgetEl.getAttribute('data-average-rating')) || 0;
      count = parseInt(widgetEl.getAttribute('data-number-of-reviews'), 10) || 0;
    }

    return { reviews, summary: { avg, count } };
  }

  function renderStars(n, max = 5) {
    let s = '';
    for (let i = 1; i <= max; i++) {
      s += `<span class="cs-rstar${i <= n ? ' is-on' : ''}" aria-hidden="true">★</span>`;
    }
    return s;
  }

  function buildHistogram(reviews) {
    const buckets = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => {
      const k = Math.max(1, Math.min(5, parseInt(r.rating, 10) || 5));
      buckets[k]++;
    });
    const total = reviews.length || 1;
    return [5, 4, 3, 2, 1].map(rating => {
      const count = buckets[rating];
      const pct = (count / total) * 100;
      const pctRounded = Math.round(pct * 10) / 10;
      return `
        <div class="cs-rhist__row" data-pct="${count === 0 ? 0 : 1}">
          <span class="cs-rhist__label">${rating}<span aria-hidden="true">★</span></span>
          <div class="cs-rhist__bar"><div class="cs-rhist__fill" style="width: ${pctRounded}%;"></div></div>
          <span class="cs-rhist__count">${count}</span>
        </div>
      `;
    }).join('');
  }

  function renderSummary(target, summary, reviews) {
    const stars = renderStars(Math.round(summary.avg));
    const avgStr = summary.avg ? summary.avg.toFixed(2).replace('.', ',') : '—';
    const countLabel = summary.count
      ? `Basé sur ${summary.count} avis vérifié${summary.count > 1 ? 's' : ''}`
      : 'Aucun avis pour le moment';
    const showHistogram = (reviews && reviews.length >= 2);
    target.innerHTML = `
      <div class="cs-reviews-cust__sum-grid">
        <div class="cs-reviews-cust__sum-main">
          <div class="cs-reviews-cust__sum-num">${avgStr}<span>/5</span></div>
          <div class="cs-reviews-cust__sum-stars">${stars}</div>
          <div class="cs-reviews-cust__sum-count">${escapeHtml(countLabel)}</div>
        </div>
        ${showHistogram ? `<div class="cs-rhist">${buildHistogram(reviews)}</div>` : ''}
      </div>
    `;
  }

  /* ---------- Mode COLUMNS (≥ 7 reviews) ---------- */

  function renderColCard(review) {
    const initial = initialFromName(review.author);
    const color = colorFromName(review.author);
    return `
      <div class="cs-rcol__card">
        <div class="cs-rcol__stars">${renderStars(review.rating || 5)}</div>
        ${review.title ? `<h4 class="cs-rcol__title">${escapeHtml(review.title)}</h4>` : ''}
        <p class="cs-rcol__body">${escapeHtml(review.body)}</p>
        <div class="cs-rcol__who">
          <div class="cs-rcol__avatar" style="background:${color}">${escapeHtml(initial)}</div>
          <div>
            <div class="cs-rcol__name">${escapeHtml(review.author)}</div>
            <div class="cs-rcol__date">${escapeHtml(review.date)}</div>
          </div>
        </div>
      </div>
    `;
  }

  function renderColumn(reviews, duration) {
    const cards = reviews.map(renderColCard).join('');
    return `
      <div class="cs-rcol" style="--cs-rcol-dur: ${duration}s">
        <div class="cs-rcol__track">
          ${cards}${cards}
        </div>
      </div>
    `;
  }

  function buildColumnsMode(stackWrap, reviews) {
    const cols = [[], [], []];
    reviews.forEach((r, i) => cols[i % 3].push(r));
    cols.forEach((col, idx) => {
      while (col.length < 3) col.push(...reviews);
      cols[idx] = col.slice(0, Math.max(3, Math.ceil(reviews.length / 3) + 2));
    });
    const durations = [70, 90, 80];
    stackWrap.innerHTML = `
      <div class="cs-reviews-cust__cols">
        ${renderColumn(cols[0], durations[0])}
        ${renderColumn(cols[1], durations[1])}
        ${renderColumn(cols[2], durations[2])}
      </div>
    `;
  }

  /* ---------- Mode EDITORIAL (≤ 6 reviews) ---------- */

  function buildEditorialMode(stackWrap, reviews, summary) {
    const titleEl = document.querySelector('.cs-buy__title, .cs-pdp__title, h1.product__title');
    const productName = (titleEl && titleEl.textContent ? titleEl.textContent : 'Cloud Store').replace(/\s+/g, ' ').trim();
    const tickerText = `CLOUD STORE CBD · AVIS VÉRIFIÉS · ${productName.toUpperCase()} · `;

    stackWrap.innerHTML = `
      <div class="cs-redit" data-cs-redit>
        <div class="cs-redit__bignum" data-cs-redit-num aria-hidden="true">01</div>

        <div class="cs-redit__inner">
          <aside class="cs-redit__rail">
            <span class="cs-redit__rail-text">Avis vérifiés</span>
            <div class="cs-redit__progress" aria-hidden="true">
              <div class="cs-redit__progress-bar" data-cs-redit-progress></div>
            </div>
            <span class="cs-redit__rail-count" data-cs-redit-count>1 / ${reviews.length}</span>
          </aside>

          <div class="cs-redit__main">
            <div class="cs-redit__topbar">
              <div class="cs-redit__badge">
                <span class="cs-redit__badge-dot"></span>
                <span class="cs-redit__badge-stars">${renderStars(Math.round(summary.avg || 5))}</span>
                <span class="cs-redit__badge-label">${(summary.avg || 5).toFixed(1).replace('.', ',')}/5 · ${summary.count || reviews.length} avis</span>
              </div>
            </div>

            <div class="cs-redit__pair" data-cs-redit-pair>
              <article class="cs-redit__quote-card" data-cs-redit-card="0">
                <blockquote class="cs-redit__quote">
                  <span data-cs-redit-quote-0></span>
                </blockquote>
                <div class="cs-redit__author">
                  <span class="cs-redit__author-line"></span>
                  <div class="cs-redit__avatar" data-cs-redit-avatar-0></div>
                  <div class="cs-redit__author-meta">
                    <div class="cs-redit__name" data-cs-redit-name-0></div>
                    <div class="cs-redit__role" data-cs-redit-role-0></div>
                  </div>
                </div>
              </article>
              <article class="cs-redit__quote-card" data-cs-redit-card="1">
                <blockquote class="cs-redit__quote">
                  <span data-cs-redit-quote-1></span>
                </blockquote>
                <div class="cs-redit__author">
                  <span class="cs-redit__author-line"></span>
                  <div class="cs-redit__avatar" data-cs-redit-avatar-1></div>
                  <div class="cs-redit__author-meta">
                    <div class="cs-redit__name" data-cs-redit-name-1></div>
                    <div class="cs-redit__role" data-cs-redit-role-1></div>
                  </div>
                </div>
              </article>
            </div>

            <div class="cs-redit__nav">
              <button type="button" class="cs-redit__navbtn" data-cs-redit-prev aria-label="Avis précédents">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <button type="button" class="cs-redit__navbtn" data-cs-redit-next aria-label="Avis suivants">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </div>
        </div>

        <div class="cs-redit__ticker" aria-hidden="true">
          <div class="cs-redit__ticker-track">
            ${escapeHtml(tickerText).repeat(8)}
          </div>
        </div>
      </div>
    `;

    initEditorial(stackWrap, reviews);
  }

  function initEditorial(root, reviews) {
    const wrap = root.querySelector('[data-cs-redit]');
    if (!wrap) return;
    const numEl = wrap.querySelector('[data-cs-redit-num]');
    const pairEl = wrap.querySelector('[data-cs-redit-pair]');
    const progressEl = wrap.querySelector('[data-cs-redit-progress]');
    const countEl = wrap.querySelector('[data-cs-redit-count]');
    const prevBtn = wrap.querySelector('[data-cs-redit-prev]');
    const nextBtn = wrap.querySelector('[data-cs-redit-next]');
    const card0 = wrap.querySelector('[data-cs-redit-card="0"]');
    const card1 = wrap.querySelector('[data-cs-redit-card="1"]');

    // Reviews per page : 1 on mobile, 2 on tablet+
    const mq = window.matchMedia('(min-width: 720px)');
    let perPage = mq.matches ? 2 : 1;
    let totalPages = Math.ceil(reviews.length / perPage);
    let page = 0;
    let timer;

    function fillCard(cardEl, idxSuffix, review) {
      const quoteEl = cardEl.querySelector(`[data-cs-redit-quote-${idxSuffix}]`);
      const nameEl  = cardEl.querySelector(`[data-cs-redit-name-${idxSuffix}]`);
      const roleEl  = cardEl.querySelector(`[data-cs-redit-role-${idxSuffix}]`);
      const avEl    = cardEl.querySelector(`[data-cs-redit-avatar-${idxSuffix}]`);
      if (!review) {
        cardEl.classList.add('is-empty');
        quoteEl.textContent = '';
        nameEl.textContent = '';
        roleEl.textContent = '';
        avEl.textContent = '';
        avEl.style.background = '';
        return;
      }
      cardEl.classList.remove('is-empty');
      quoteEl.textContent = `« ${review.body} »`;
      nameEl.textContent = review.author;
      roleEl.textContent = review.date || 'Avis vérifié';
      avEl.textContent = initialFromName(review.author);
      avEl.style.background = colorFromName(review.author);
    }

    function update() {
      const a = reviews[page * perPage];
      const b = perPage === 2 ? reviews[page * perPage + 1] : null;
      const n = String(page + 1).padStart(2, '0');

      [numEl, card0, card1].forEach(el => el && el.classList.add('is-leaving'));

      setTimeout(() => {
        numEl.textContent = n;
        fillCard(card0, '0', a);
        fillCard(card1, '1', b);
        countEl.textContent = `${page + 1} / ${totalPages}`;
        progressEl.style.height = `${((page + 1) / totalPages) * 100}%`;
        [numEl, card0, card1].forEach(el => el && el.classList.remove('is-leaving'));
      }, 220);
    }

    function next() { page = (page + 1) % totalPages; update(); restart(); }
    function prev() { page = (page - 1 + totalPages) % totalPages; update(); restart(); }

    function restart() {
      clearInterval(timer);
      if (totalPages > 1) timer = setInterval(() => { page = (page + 1) % totalPages; update(); }, EDITORIAL_AUTOPLAY_MS);
    }

    // Recompute on viewport breakpoint change
    function handleMqChange(e) {
      perPage = e.matches ? 2 : 1;
      totalPages = Math.ceil(reviews.length / perPage);
      page = 0;
      update();
      restart();
    }
    if (mq.addEventListener) mq.addEventListener('change', handleMqChange);
    else if (mq.addListener) mq.addListener(handleMqChange);

    prevBtn && prevBtn.addEventListener('click', prev);
    nextBtn && nextBtn.addEventListener('click', next);

    wrap.addEventListener('mouseenter', () => clearInterval(timer));
    wrap.addEventListener('mouseleave', restart);

    // Magnetic parallax — desktop only (skip on touch devices to save cycles)
    const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (!isTouch) {
      wrap.addEventListener('mousemove', (e) => {
        const rect = wrap.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = (e.clientX - cx) / rect.width;
        const dy = (e.clientY - cy) / rect.height;
        numEl.style.transform = `translate(${dx * 24}px, ${dy * 14}px)`;
      });
      wrap.addEventListener('mouseleave', () => { numEl.style.transform = ''; });
    }

    update();
    restart();
  }

  /* ---------- Common build / dispatch ---------- */

  function buildCarousel(section, data) {
    const sourceEl = section.querySelector(SELECTORS.source);
    const summaryEl = section.querySelector(SELECTORS.summary);
    const stackWrap = section.querySelector(SELECTORS.stackWrap);
    const stackEl = section.querySelector(SELECTORS.stack);
    const dotsEl = section.querySelector(SELECTORS.dots);
    const ctaEl = section.querySelector(SELECTORS.cta);

    if (dotsEl) dotsEl.style.display = 'none';
    section.querySelectorAll(SELECTORS.nav).forEach((b) => b.style.display = 'none');

    if (!data.reviews.length) {
      // Empty state : ONE unified card (no separate summary, no separate CTA)
      section.classList.add('cs-pdp-reviews-wrap--empty');
      if (summaryEl) summaryEl.innerHTML = '';
      if (stackWrap) stackWrap.innerHTML = `
        <div class="cs-reviews-empty" data-cs-reviews-empty>
          <div class="cs-reviews-empty__stars" aria-hidden="true">${renderStars(0)}</div>
          <h3 class="cs-reviews-empty__title">Pas encore d'avis sur ce produit</h3>
          <p class="cs-reviews-empty__text">Sois la première personne à partager ton ressenti — ça aide énormément la prochaine personne qui hésite 🌿</p>
          <button type="button" class="cs-reviews-cust__write-btn" data-cs-reviews-write>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
            </svg>
            <span>Soyez le premier à laisser un avis</span>
          </button>
        </div>
      `;
      // Hide the duplicate CTA below
      if (ctaEl) ctaEl.innerHTML = '';
      // Wire up the inline button to Judge.me's write form
      const innerBtn = section.querySelector('[data-cs-reviews-empty] [data-cs-reviews-write]');
      if (innerBtn) innerBtn.addEventListener('click', () => triggerJdgmWrite(sourceEl));
      if (sourceEl) sourceEl.classList.add('is-source-hidden');
      return;
    }

    section.classList.remove('cs-pdp-reviews-wrap--empty');
    if (summaryEl) renderSummary(summaryEl, data.summary, data.reviews);

    if (stackWrap) {
      if (data.reviews.length <= EDITORIAL_THRESHOLD) {
        buildEditorialMode(stackWrap, data.reviews, data.summary);
      } else {
        buildColumnsMode(stackWrap, data.reviews);
      }
    }

    if (ctaEl) ensureWriteCta(ctaEl, sourceEl, data.reviews.length);

    if (sourceEl) sourceEl.classList.add('is-source-hidden');
  }

  function ensureWriteCta(target, sourceEl, reviewCount) {
    const ctaLabel = reviewCount === 0 ? 'Soyez le premier à laisser un avis' : 'Laisser un avis';
    target.innerHTML = `
      <button type="button" class="cs-reviews-cust__write-btn" data-cs-reviews-write>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
        </svg>
        <span>${ctaLabel}</span>
      </button>
      <p class="cs-reviews-cust__write-hint">Avis vérifié · publié après modération</p>
    `;

    const btn = target.querySelector('[data-cs-reviews-write]');
    btn.addEventListener('click', () => triggerJdgmWrite(sourceEl));

    // Try replacing with a real Judge.me link if their widget exposes one (for accessibility/parity)
    if (sourceEl) {
      let tries = 0;
      const t = setInterval(() => {
        tries++;
        const writeLink = sourceEl.querySelector('.jdgm-write-rev-link');
        if (writeLink) {
          // keep our visual styling but route the click to Judge.me's link
          btn.dataset.csHasJdgm = '1';
          clearInterval(t);
        } else if (tries > 40) {
          clearInterval(t);
        }
      }, 250);
    }
  }

  // Pre-warm Judge.me's write-review handler — fait que le 1er clic visiteur ouvre directement
  // la modal (sinon Judge.me bind son handler seulement au 1er clic, donc le clic réel est perdu).
  function preWarmJdgm() {
    const link = document.querySelector('.jdgm-write-rev-link');
    if (!link) return false;
    // Force la ligne d'événements à s'établir : Judge.me écoute soit en délégation soit en binding direct.
    // On stimule un focus + dispatch d'event silencieux pour forcer l'attach sans déclencher la modal.
    try {
      // Walk parents and ensure not display:none for binding
      let parent = link.parentElement;
      while (parent && parent !== document.body) {
        const cs = window.getComputedStyle(parent);
        if (cs.display === 'none' || cs.visibility === 'hidden') {
          const orig = parent.getAttribute('style') || '';
          parent.style.cssText = orig + ';display:block !important;visibility:visible !important;position:absolute !important;left:-99999px !important;top:-99999px !important;height:0 !important;overflow:hidden !important;pointer-events:none !important';
        }
        parent = parent.parentElement;
      }
      // Dispatch un mouseenter/focus pour stimuler le rebind
      link.dispatchEvent(new Event('mouseenter', { bubbles: true }));
      // Re-init Judge.me si leur API est dispo (force le rebind global)
      if (window.jdgm && typeof window.jdgm.init === 'function') {
        try { window.jdgm.init(); } catch (e) {}
      }
      if (window.jdgm && typeof window.jdgm.installWidget === 'function') {
        try { window.jdgm.installWidget(); } catch (e) {}
      }
      if (window.jdgm && typeof window.jdgm.installWidgets === 'function') {
        try { window.jdgm.installWidgets(); } catch (e) {}
      }
      return true;
    } catch (e) { return false; }
  }

  // Lance le pre-warm dès que Judge.me est chargé (max 8 tentatives, toutes les 500ms)
  function schedulePreWarm() {
    let tries = 0;
    const t = setInterval(() => {
      tries++;
      if (preWarmJdgm() || tries >= 16) clearInterval(t);
    }, 500);
  }

  function triggerJdgmWrite() {
    // Ouvre notre modal custom — fini le combat avec Judge.me
    const modal = document.querySelector('[data-cs-revmodal]');
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // focus le 1er bouton étoile pour accessibilité
    setTimeout(() => {
      const firstStar = modal.querySelector('[data-cs-revmodal-star]');
      if (firstStar) firstStar.focus();
    }, 50);
  }

  // Init de la modal custom : stars rating + close handlers
  function initRevModal() {
    const modal = document.querySelector('[data-cs-revmodal]');
    if (!modal) return;

    const closeEls = modal.querySelectorAll('[data-cs-revmodal-close]');
    const stars = modal.querySelectorAll('[data-cs-revmodal-star]');
    const ratingInput = modal.querySelector('[data-cs-revmodal-rating-input]');
    const ratingLabel = modal.querySelector('[data-cs-revmodal-rating-label]');
    const form = modal.querySelector('#cs-revmodal-form');

    function close() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    closeEls.forEach(el => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    const labels = ['', 'Décevant', 'Mitigé', 'Correct', 'Très bon', 'Excellent !'];
    stars.forEach((star) => {
      const value = parseInt(star.getAttribute('data-value'), 10);
      star.addEventListener('click', () => {
        ratingInput.value = value;
        stars.forEach((s) => {
          const v = parseInt(s.getAttribute('data-value'), 10);
          s.classList.toggle('is-on', v <= value);
          s.setAttribute('aria-checked', v === value ? 'true' : 'false');
        });
        if (ratingLabel) ratingLabel.textContent = labels[value];
      });
      star.addEventListener('mouseenter', () => {
        stars.forEach((s) => {
          const v = parseInt(s.getAttribute('data-value'), 10);
          s.classList.toggle('is-hover', v <= value);
        });
        if (ratingLabel && !ratingInput.value) ratingLabel.textContent = labels[value];
      });
      star.addEventListener('mouseleave', () => {
        stars.forEach((s) => s.classList.remove('is-hover'));
        if (ratingLabel) ratingLabel.textContent = ratingInput.value ? labels[parseInt(ratingInput.value, 10)] : 'Sélectionne une note';
      });
    });

    // Validation : empêche de submit sans note
    if (form) {
      form.addEventListener('submit', (e) => {
        if (!ratingInput.value) {
          e.preventDefault();
          if (ratingLabel) {
            ratingLabel.textContent = '⚠️ Choisis d\'abord une note';
            ratingLabel.classList.add('is-error');
          }
        }
      });
    }

    // Si on arrive avec form.posted_successfully? affiché, ouvre la modal au load
    if (modal.querySelector('[data-cs-revmodal-success]')) {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
    }
  }

  function showError(section, message) {
    const stackEl = section.querySelector(SELECTORS.stack);
    const summaryEl = section.querySelector(SELECTORS.summary);
    if (summaryEl) summaryEl.innerHTML = '';
    if (stackEl) stackEl.innerHTML = `<div class="cs-reviews-cust__empty">${escapeHtml(message)}</div>`;
  }

  async function init() {
    const sections = document.querySelectorAll(SELECTORS.section);
    for (const section of sections) {
      const token = section.getAttribute('data-cs-jdgm-token');
      const shopDomain = section.getAttribute('data-cs-shop-domain');
      const productId = section.getAttribute('data-cs-product-id');

      if (!token || !shopDomain || !productId) {
        showError(section, 'Configuration des avis manquante.');
        continue;
      }

      try {
        const html = await fetchWidgetHTML({ token, shopDomain, productId, perPage: 50 });
        const data = parseReviewsFromHTML(html);
        buildCarousel(section, data);
      } catch (err) {
        console.error('[cs-reviews] fetch failed', err);
        showError(section, 'Impossible de charger les avis pour le moment.');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); initRevModal(); });
  } else {
    init();
    initRevModal();
  }
})();
